import { Marke } from './Marke.js'
import { ApiError } from '../net/errors.js'

/**
 * Die Seite für einen stillgelegten Betrieb.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ACHTUNG: Diese Seite hat seit dem 11. Oktober 2026 keinen Fall mehr
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Der Gründerentscheid macht aus ARCHIVIERT den LESEMODUS: Die Anmeldung wird
 * nicht mehr abgewiesen (`services/auth.ts`), der Betrieb kommt herein, liest,
 * exportiert und kann zahlen. Damit kommt der 403, auf den diese Seite
 * antwortet, im Anmeldefluss nicht mehr vor.
 *
 * Sie steht trotzdem noch hier, und zwar aus einem Grund, der nichts mit
 * Fachlichkeit zu tun hat: Der Release-Läufer überträgt Dateien, er löscht
 * keine. Eine gelöschte Datei kann er weder ankündigen noch committen. Das
 * Entfernen gehört deshalb in den nächsten Durchgang, in dem auch eine
 * Löschung übergeben werden kann – als eigener, sichtbarer Schritt und nicht
 * als stiller Nebeneffekt.
 *
 * Bis dahin ist der Zweig in `Login.tsx` eine Vorsichtsmassnahme: Antwortet
 * ein Server doch einmal mit `BETRIEB_ARCHIVIERT` auf eine Anmeldung, steht
 * hier eine Erklärung statt eines nackten 403.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * Warum sie im Anmeldefluss steht und nicht im Büro
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Weil ein stillgelegter Betrieb bis dahin nicht ins Büro kam. `auth.ts` wies
 * die ANMELDUNG ab – es gab keine Sitzung, hinter der eine Seite liegen
 * könnte.
 * Bis D7 war das ein nackter 403 mit einem Satz; wer ihn sah, wusste nicht,
 * warum, seit wann, und ob er etwas tun kann.
 *
 * Alles, was hier steht, kommt deshalb aus der Absage selbst
 * (`details` des 403). Diese Seite rechnet nichts und rät nichts: Fehlt eine
 * Angabe, steht sie nicht da.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * Was hier NICHT steht
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Ein Löschdatum – solange der Server keines herausgibt, und das tut er
 * nicht, bevor der sichere Purge gebaut ist. Und keine Zusage, dass eine
 * Zahlung den Zugang sofort öffnet: Was dafür nötig ist, prüft der Server an
 * einer bezahlten Stripe-Rechnung, nicht diese Seite.
 */

/** Woran es lag, in einem Satz. `null` heisst: der Server hat es nicht gesagt. */
function grundsatz(sperrgrund: string | null): string | null {
  switch (sperrgrund) {
    case 'test_abgelaufen':
      return 'Die Testphase ist abgelaufen und es wurde kein Abo gestartet.'
    case 'zahlung_offen':
      return 'Es ist keine erfolgreiche Zahlung eingegangen.'
    case 'abo_beendet':
      return 'Das Abo ist beendet.'
    default:
      return null
  }
}

function text(w: unknown): string | null {
  return typeof w === 'string' && w.trim() !== '' ? w : null
}

/** «2027-03-17» als «17.03.2027». */
function datum(iso: string): string {
  const [j, m, t] = iso.split('-')
  return t && m && j ? `${t}.${m}.${j}` : iso
}

/**
 * Trägt diese Absage eine Stilllegung?
 *
 * Steht hier und nicht im Login-Bildschirm, damit die Abfrage an einer Stelle
 * ist: Ein zweiter Ort, der `BETRIEB_ARCHIVIERT` prüft, ist der Ort, an dem
 * die Prüfung eines Tages fehlt.
 */
export function istStillgelegt(fehler: unknown): fehler is ApiError {
  return fehler instanceof ApiError && fehler.grund === 'BETRIEB_ARCHIVIERT'
}

export function Stillgelegt({
  fehler,
  aufZurueck
}: {
  fehler: ApiError
  /** Zurück zur Anmeldung – etwa um es mit einem anderen Zugang zu versuchen. */
  aufZurueck: () => void
}): JSX.Element {
  const a = fehler.angaben ?? {}
  const betrieb = text(a.betrieb)
  const satz = grundsatz(text(a.sperrgrund))
  const gesperrtSeit = text(a.gesperrtSeit)
  const endeAm = text(a.endeAm)
  const loeschung = text(a.loeschungVorgesehenAm)
  const zahlungsweg = a.zahlungswegVorhanden === true

  return (
    <div className="rp-an-seite" id="stillgelegt">
      <div className="rp-an-karte">
        <Marke />
        <h1>Zugang stillgelegt</h1>

        <p id="stillgelegt-satz">
          {betrieb ? `Der Betrieb «${betrieb}» ist stillgelegt. ` : 'Dieser Betrieb ist stillgelegt. '}
          {satz ?? ''}
        </p>

        <dl className="o-werte" id="stillgelegt-daten">
          {endeAm && (
            <div>
              <dt>Ende der Phase</dt>
              <dd id="stillgelegt-ende">{datum(endeAm)}</dd>
            </div>
          )}
          {gesperrtSeit && (
            <div>
              <dt>Gesperrt seit</dt>
              <dd id="stillgelegt-seit">{datum(gesperrtSeit)}</dd>
            </div>
          )}
          {/*
            Nur wenn der Server es herausgibt. Siehe oben: ein Löschdatum ist
            eine Zusage.
          */}
          {loeschung && (
            <div>
              <dt>Löschung vorgesehen</dt>
              <dd id="stillgelegt-loeschung">{datum(loeschung)}</dd>
            </div>
          )}
        </dl>

        <p className="o-soft">
          Deine Daten sind vorhanden. Zum Arbeiten ist der Zugang geschlossen – auch das
          Ansehen und der Export laufen über uns.
        </p>

        {zahlungsweg ? (
          <p className="o-soft" id="stillgelegt-zahlung">
            Wenn du weiterarbeiten willst: Sobald eine Zahlung über das bestehende Abo
            erfolgreich durchgeht, öffnet sich der Zugang wieder. Melde dich bei uns, wir
            richten es mit dir ein.
          </p>
        ) : (
          <p className="o-soft" id="stillgelegt-kontakt">
            Für diesen Betrieb ist keine Abrechnung hinterlegt. Melde dich bei uns, dann
            klären wir, wie es weitergeht.
          </p>
        )}

        <p className="o-soft">
          <a href="mailto:info@raporto.ch">info@raporto.ch</a>
        </p>

        <button type="button" className="o-btn" id="stillgelegt-zurueck" onClick={aufZurueck}>
          Zurück zur Anmeldung
        </button>
      </div>
    </div>
  )
}
